/**
 * The hello-world page, built to be genuinely useful the moment CapCom finishes
 * provisioning a deployment: it answers "which client DB am I on, did the SQL
 * firewall rule work, is my session secret real, and which image is this?" from a
 * browser, without shelling into the container.
 */
import { useCallback, useEffect, useState } from 'react'
import type { InitPayload, ProbeState } from './types'

const API = '/LearnerView/api'

const C = {
  bg: '#0f1419',
  card: '#171d24',
  border: '#232c36',
  text: '#e6edf3',
  dim: '#8b98a5',
  ok: '#3fb950',
  fail: '#f85149',
  warn: '#d29922',
  accent: '#58a6ff',
}

function Dot({ state }: { state: 'ok' | 'fail' | 'pending' }) {
  const color = state === 'ok' ? C.ok : state === 'fail' ? C.fail : C.dim
  return (
    <span
      style={{
        display: 'inline-block',
        width: 9,
        height: 9,
        borderRadius: '50%',
        background: color,
        boxShadow: state === 'ok' ? `0 0 8px ${C.ok}` : 'none',
        marginRight: 8,
        flexShrink: 0,
      }}
    />
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 16, padding: '7px 0', borderBottom: `1px solid ${C.border}` }}>
      <div style={{ color: C.dim, minWidth: 190, fontSize: 13 }}>{label}</div>
      <div style={{ fontSize: 13, wordBreak: 'break-all', fontFamily: 'ui-monospace, monospace' }}>
        {children}
      </div>
    </div>
  )
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        padding: '18px 22px',
        marginBottom: 18,
      }}
    >
      <h2 style={{ margin: '0 0 10px', fontSize: 13, letterSpacing: 1.2, color: C.dim, fontWeight: 600 }}>
        {title.toUpperCase()}
      </h2>
      {children}
    </section>
  )
}

export default function App() {
  const [init, setInit] = useState<InitPayload | null>(null)
  const [initError, setInitError] = useState<string | null>(null)
  const [probe, setProbe] = useState<ProbeState>({
    health: 'pending',
    ping: 'pending',
    pingLatencyMs: null,
    pingError: null,
  })

  useEffect(() => {
    fetch(`${API}/init`)
      .then(async (r) => {
        if (!r.ok) throw new Error(`init returned ${r.status}`)
        return (await r.json()) as InitPayload
      })
      .then(setInit)
      .catch((e: unknown) => setInitError(e instanceof Error ? e.message : String(e)))
  }, [])

  const runProbes = useCallback(async () => {
    let health: 'ok' | 'fail' = 'fail'
    try {
      const r = await fetch(`${API}/health`)
      health = r.ok ? 'ok' : 'fail'
    } catch {
      // Leave as 'fail' — an unreachable process is indistinguishable from a bad one.
    }

    let ping: 'ok' | 'fail' = 'fail'
    let pingLatencyMs: number | null = null
    let pingError: string | null = null
    try {
      const r = await fetch(`${API}/ping`)
      const body = (await r.json()) as { db?: { latencyMs?: number; error?: string | null } }
      ping = r.ok ? 'ok' : 'fail'
      pingLatencyMs = body.db?.latencyMs ?? null
      pingError = r.ok ? null : (body.db?.error ?? `ping returned ${r.status}`)
    } catch (e) {
      pingError = e instanceof Error ? e.message : String(e)
    }
    setProbe({ health, ping, pingLatencyMs, pingError })
  }, [])

  useEffect(() => {
    void runProbes()
    const t = setInterval(() => void runProbes(), 5000)
    return () => clearInterval(t)
  }, [runProbes])

  return (
    <div
      style={{
        minHeight: '100vh',
        background: C.bg,
        color: C.text,
        font: '15px/1.5 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '48px 24px',
      }}
    >
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <h1 style={{ margin: 0, fontSize: 30, fontWeight: 650 }}>Hello from the learner view</h1>
        <p style={{ color: C.dim, marginTop: 8, marginBottom: 32 }}>
          Template site for TC LMS React learner view deployments. If you are reading this over
          HTTPS on a client hostname, CapCom provisioned this container end to end.
        </p>

        {initError && (
          <Card title="init failed">
            <div style={{ color: C.fail, fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>
              {initError}
            </div>
            <p style={{ color: C.dim, fontSize: 13, marginBottom: 0 }}>
              A 500 here almost always means a required environment variable is missing or
              DEFAULT_PROGRAM_ID is wrong. Check the container logs.
            </p>
          </Card>
        )}

        <Card title="probes">
          <div style={{ display: 'flex', alignItems: 'center', padding: '7px 0' }}>
            <Dot state={probe.health} />
            <code style={{ fontSize: 13 }}>GET {API}/health</code>
            <span style={{ color: C.dim, fontSize: 12, marginLeft: 10 }}>liveness · DB-free</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', padding: '7px 0' }}>
            <Dot state={probe.ping} />
            <code style={{ fontSize: 13 }}>GET {API}/ping</code>
            <span style={{ color: C.dim, fontSize: 12, marginLeft: 10 }}>
              readiness · SELECT 1
              {probe.pingLatencyMs !== null && ` · ${probe.pingLatencyMs}ms`}
            </span>
          </div>
          {probe.pingError && (
            <div style={{ color: C.fail, fontSize: 12, fontFamily: 'ui-monospace, monospace', marginTop: 8 }}>
              {probe.pingError}
              <div style={{ color: C.dim, marginTop: 6, fontFamily: 'inherit' }}>
                If this is a connection timeout, the SQL firewall rule for this Container Apps
                environment&apos;s egress IP is the first thing to check.
              </div>
            </div>
          )}
        </Card>

        {init && (
          <>
            {init.warnings.length > 0 && (
              <Card title="contract warnings">
                {init.warnings.map((w) => (
                  <div key={w} style={{ color: C.warn, fontSize: 13, padding: '4px 0' }}>
                    {w}
                  </div>
                ))}
              </Card>
            )}

            <Card title="database">
              <Row label="server">{init.db.server}:{init.db.port}</Row>
              <Row label="database (requested)">{init.db.database}</Row>
              <Row label="database (actual)">
                {init.db.databaseNameActual ?? <span style={{ color: C.dim }}>—</span>}
                {init.db.databaseNameActual &&
                  init.db.databaseNameActual.toLowerCase() !== init.db.database.toLowerCase() && (
                    <span style={{ color: C.fail, marginLeft: 10 }}>mismatch</span>
                  )}
              </Row>
              <Row label="user">{init.db.user}</Row>
              <Row label="password">
                {init.db.passwordConfigured ? (
                  <span style={{ color: C.ok }}>configured</span>
                ) : (
                  <span style={{ color: C.fail }}>missing</span>
                )}
              </Row>
              <Row label="server version">
                {init.db.serverVersion ?? <span style={{ color: C.dim }}>—</span>}
              </Row>
              <Row label="DomainLanguageURLs">
                {init.lms.domainLanguageUrlsPresent ? (
                  <span style={{ color: C.ok }}>
                    present · {init.lms.domainLanguageUrlsRowCount} rows
                  </span>
                ) : (
                  <span style={{ color: C.warn }}>not found — is this an LMS database?</span>
                )}
              </Row>
            </Card>

            <Card title="request">
              <Row label="host">{init.request.host}</Row>
              <Row label="x-forwarded-proto">
                {init.request.forwardedProto ?? (
                  <span style={{ color: C.dim }}>none — not behind ACA ingress</span>
                )}
              </Row>
              <Row label="program id">
                {init.program.id}
                <span style={{ color: C.dim, marginLeft: 10 }}>({init.program.source})</span>
              </Row>
              <Row label="session secret">
                {init.session.secretConfigured ? (
                  <span style={{ color: C.ok }}>configured</span>
                ) : (
                  <span style={{ color: C.warn }}>dev default</span>
                )}
              </Row>
            </Card>

            <Card title="build">
              <Row label="image sha">
                <span style={{ color: C.accent }}>{init.app.sha}</span>
              </Row>
              <Row label="NODE_ENV">{init.app.nodeEnv}</Row>
              <Row label="node">{init.app.nodeVersion}</Row>
              <Row label="port">{init.app.port}</Row>
              <Row label="serve static">{String(init.app.serveStatic)}</Row>
              <Row label="started">{init.app.startedAt}</Row>
              <Row label="uptime">{init.app.uptimeSec}s</Row>
            </Card>
          </>
        )}
      </div>
    </div>
  )
}
